/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'../../../DynamicFileLoader',
	'./InlineFeatures',
	'underscore'
], function (DynamicFileLoader, inlineFeatures,  _) {
	'use strict';

	/**
  * @deprecated Replaced by dashboard-core/js/features/FeatureLoader.js
  */
	class FeatureLoader {

		constructor(widgetApi, dashboardApi) {
			this.widgetApi = widgetApi;
			this.dashboardApi = dashboardApi;
			this.features = {};
			this.disabledFeatures = {};
		}

		/**
		*	Load the live widget features
		* @param Array(String) featureSet - list of feature to be loaded. If no featureSet is defined, then we load everything.
		*
		*/
		loadFeatures(featureSet) {
			return this.dashboardApi.findGlassCollection('com.ibm.bi.dashboard.live-features').then((collectionItems) => {
				const promises = [];

				if(collectionItems) {
					// Adde the inline features. Inline features are a quick and easy way to test features without having to add the perspective json
					collectionItems = collectionItems.concat(inlineFeatures);

					collectionItems.forEach((feature) => {
						const identifier = feature.name || feature.id;
						if (!featureSet || featureSet.indexOf(identifier) !== -1) {
							promises.push(
								DynamicFileLoader.load([feature.class])
									.then((modules) => {
										this.features[identifier] = new modules[0](this.widgetApi, this.dashboardApi);
									})
							);
						}
					});
				}
				return Promise.all(promises);
			});
		}

		setFeatureEnabled(name, isEnabled) {
			if (isEnabled) {
				delete this.disabledFeatures[name];
			} else {
				this.disabledFeatures[name] = true;
			}
		}

		unloadFeatures() {
			_.each(_.keys(this.features), (featureName) => {
				if (this.features[featureName].destroy) {
					this.features[featureName].destroy();
				}
				delete this.features[featureName];
			});
			this.features={};
		}

		setFeature(name, feature){
			this.features[name] = feature;
		}

		getFeature(name) {
			var api;
			if (this.features[name] && this.isFeatureEnabled(name) && this.features[name].getAPI) {
				api = this.features[name].getAPI();
			}
			return api;
		}

		isFeatureEnabled(name) {
			return !this.disabledFeatures[name] && (!this.features[name].isEnabled || this.features[name].isEnabled());
		}

		/**
		 * @param tagToMatch - a tag that any feature must include in its getTags() api.
		 * @returns any features that match the tag passed in as an array (empty array is returned if no features match).
		 */
		getMatchingFeatures(tagToMatch) {
			let matchingFeatures = [];
			_.each(_.keys(this.features), (featureName) => {
				const feature = this.features[featureName];
				if (feature && this.isFeatureEnabled(featureName) &&
						feature.getFeatureTags && feature.getFeatureTags().indexOf(tagToMatch) >= 0) {
					const featureAPI = this.getFeature(featureName);
					if (featureAPI) {
						matchingFeatures.push(featureAPI);
					}
				}
			});

			return matchingFeatures;
		}

		/**
		 * Enable or disable a feature that matches the supplied tag
		 * @param tagToMatch - a tag that any feature must include in its getTags() api.
		 * @param isEnabled
		 */
		setMatchingFeaturesEnabled(tagToMatch, isEnabled) {
			_.each(_.keys(this.features), (featureName) => {
				const feature = this.features[featureName];
				if (feature &&
						feature.getFeatureTags && feature.getFeatureTags().indexOf(tagToMatch) >= 0) {
					this.setFeatureEnabled(featureName, isEnabled);
				}
			});
		}

		getExtraRenderSequenceSteps() {
			var steps = [];
			for (var name in this.features) {
				if (this.isFeatureEnabled(name) && this.features[name].getExtraRenderSequenceSteps) {
					let extraSteps =this.features[name].getExtraRenderSequenceSteps();
					if(extraSteps) {
						steps.push.apply(steps, Array.isArray(extraSteps) ? extraSteps : [extraSteps]);
					}
				}
			}
			return steps;
		}

		/**
		 * Return an array of render sequence step names that are associated with the supplied tag.
		 * If enabledFeaturesOnly is set to true, only return the steps associated with enabled features.
		 * @param tagToMatch - a tag hat any feature must include in its getTags() api.
		 * @param enabledFeaturesOnly (default false) - if set, only steps for enabled features will be returned.
		 * @returns an array of step ids for the renderSequence steps that are associated with the features that match the tag.
		 */
		getMatchingFeatureStepIds(tagToMatch, enabledFeaturesOnly=false) {
			var stepIds = [];
			for (var name in this.features) {
				if (!enabledFeaturesOnly || this.isFeatureEnabled(name)) {
					const feature = this.features[name];
					if (feature && feature.getFeatureTags && feature.getExtraRenderSequenceSteps &&
						feature.getFeatureTags().indexOf(tagToMatch) >= 0) {
						let extraSteps =this.features[name].getExtraRenderSequenceSteps();
						if(extraSteps) {
							extraSteps = Array.isArray(extraSteps) ? extraSteps : [extraSteps];
							stepIds.push.apply(stepIds, extraSteps.map((step) => step.id));
						}
					}
				}
			}
			return stepIds;
		}

	}

	return FeatureLoader;
});
